import { colors } from '@mathflat/common/style'
import { essayScoringApi } from '@mathflat/domain/EssayScoring'
import type { NotificationCta } from '@mathflat/domain/Notification'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import type { MouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'
import Divider from 'src/component/atoms/Divider'
import { Icon } from 'src/component/atoms/Icon'
import type { IconType } from 'src/component/atoms/Icon/Icon'
import { DrawerTemplate } from 'src/component/templates/DrawerTemplate'
import type { Notification as NotificationDTO, NotificationCTAOption } from 'src/dto'

import useInfiniteScroll from '@/hooks/useInfiniteScroll'
import useRepository from '@/hooks/useRepository'
import useScrollbars from '@/hooks/useScrollbars'
import { LessonGroupService } from '@/page/Lesson/_service'
import { AchievementAnalysisService } from '@/page/Lesson/AchievementAnalysis/_service/AchievementAnalysis.service.ts'
import ReportListService from '@/page/Lesson/Report/ReportList/ReportList.service'
import commonRepo from '@/repository/common.repo'
import {
  RouteName,
  수능_콘텐츠_업데이트_경로,
  시중교재전체지원_콘텐츠업데이트_경로,
} from '@/router'
import StudentGroupAccordionService from '@/service/StudentGroupAccordion.service'
import WebviewService from '@/service/webview.service'
import appPostMsg from '@/utils/appPostMsg'
import { downloadFileByUrl } from '@/utils/download'
import { mathflatUtils } from '@/utils/mathflatUtils'
import { commonErrorMessage } from '@/utils/toast'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import 새알림없음 from '../../emptyList/새알림없음'
import NotificationService from './Notification.service'
import S from './Notification.style'
import NotificicationItem from './NotificationItem'
import { isInAppPath } from './notificationLink'
import { getNotificationMessageParts } from './notificationMessage'
import { releaseNotificationUtils } from './notificationUtils'

const Notification = () => {
  const ref = useRef<HTMLDivElement | null>(null)
  const service = useRepository(NotificationService)
  const { scrollToTop, el } = useScrollbars()
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const popupTimeout = useRef<number | null>(null)
  useInfiniteScroll(ref, () => service.fetchMoreNotifications())

  const handleClose = () => {
    scrollToTop()
    service.close()
  }
  const handleClickTop = (e) => {
    e.preventDefault()
    scrollToTop()
    service.clearNewNotiCountSinceOpen()
    setShowPopup(false)
  }

  useEffect(() => {
    if (!commonRepo.academy || commonRepo.isSchoolflat) return

    releaseNotificationUtils
      .initNotificationData('ESSAY_WORKSHEET_OPEN_NOTIFICATION', new Date('2025-12-31'))
      .then((res) => {
        if (!res) return

        const { startDate, expiredDate, isRead } = res
        service.essayWorksheetNotification = releaseNotificationUtils.createEssayNotification({
          startDate,
          expiredDate,
          isRead,
        })
      })

    releaseNotificationUtils
      .initNotificationData('PUBLIC_WORKBOOK_OPEN_NOTIFICATION', new Date('2025-11-26'))
      .then((res) => {
        if (!res) return

        const { startDate, expiredDate, isRead } = res
        service.publicWorkbookSupportedNotification =
          releaseNotificationUtils.createPublicWorkbookOpenNotification({
            startDate,
            expiredDate,
            isRead,
          })
      })
  }, [commonRepo.academy, commonRepo.isSchoolflat, service])

  useEffect(() => {
    if (service.newNotiCountSinceOpen > 0 && el && el.clientHeight < el.scrollHeight) {
      setShowPopup(true)

      popupTimeout.current = window.setTimeout(() => {
        setShowPopup(false)
      }, 3000)
    }
  }, [service.newNotiCountSinceOpen])

  useEffect(() => {
    return () => {
      popupTimeout.current && window.clearTimeout(popupTimeout.current)
    }
  }, [])
  return (
    <DrawerTemplate isOpen={service.isOpen} onClose={handleClose} css={S.Template}>
      <div className={clsx('new-noti-popup', showPopup && 'pop')}>
        <div>
          <span>
            새로운 알림 <span>{service.newNotiCountSinceOpen}건</span>이 있어요
          </span>
          <button onClick={handleClickTop}>맨 위로</button>
        </div>
      </div>
      <S.Header>
        <button onClick={handleClose} className="close-button">
          <Icon name="close" color="var(--Colors-Gray-900)" size={28} />
        </button>
        <div className="title">
          <strong>알림센터</strong>
          {!service.isEmptyUnexpiredNotifications && (
            <S.ReadAllButton onClick={async () => await service.readAll()}>
              <Icon name="check" color="var(--Colors-Gray-600)" /> 전체 읽음
            </S.ReadAllButton>
          )}
        </div>
      </S.Header>
      <Scrollbars className="table-scroll-body">
        <S.Section>
          {service.isEmptyUnexpiredNotifications ? (
            <새알림없음 />
          ) : (
            <>
              {service.todayNotifications.map(
                ({
                  sortKey,
                  isRead,
                  message,
                  title,
                  formatToNotificationDatetime,
                  state,
                  id,
                  case2,
                  ctas,
                  payload,
                }) => {
                  const title1 = payload.groupName ?? ''
                  const title2 = title ?? ''
                  return (
                    <NotificicationItem
                      key={sortKey}
                      title1={title1}
                      title2={title2}
                      ctaOptions={GetCtaOptions(case2, sortKey, ctas, id, payload)}
                      iconOption={getIconOption(state, case2, payload)}
                      messageParts={getNotificationMessageParts(payload, message)}
                      {...{ isRead, formatToNotificationDatetime }}
                    />
                  )
                },
              )}
              {service.pastNotifications.length > 0 && (
                <div
                  className={clsx(
                    'horizontal-divider',
                    service.todayNotifications.length === 0 && 'no-data-today',
                  )}
                >
                  이전 알림 <Divider type="horizontal" size={313} color="var(--Colors-Gray-400)" />
                </div>
              )}
              {service.pastNotifications.map(
                ({
                  sortKey,
                  isRead,
                  message,
                  title,
                  formatToNotificationDatetime,
                  state,
                  id,
                  payload,
                  case2,
                  ctas,
                }) => {
                  const title1 = payload.groupName ?? ''
                  const title2 = title ?? ''

                  return (
                    <NotificicationItem
                      key={sortKey}
                      title1={title1}
                      title2={title2}
                      ctaOptions={GetCtaOptions(case2, sortKey, ctas, id, payload)}
                      iconOption={getIconOption(state, case2, payload)}
                      messageParts={getNotificationMessageParts(payload, message)}
                      {...{ isRead, formatToNotificationDatetime }}
                    />
                  )
                },
              )}
            </>
          )}
          <div ref={ref} />
        </S.Section>
      </Scrollbars>
    </DrawerTemplate>
  )
}

const getIconOption = (
  state: NotificationDTO['state'],
  case2: NotificationDTO['case2'],
  payload: NotificationDTO['payload'],
): { name: IconType; color?: string; size?: number } => {
  switch (state) {
    case 'FAILURE': {
      return {
        name: 'failure',
        color: colors.red.$300,
        size: 24,
      }
    }
  }

  if (payload?.eventType === 'STUDENT_WORKSHEET_SUBMIT') {
    return {
      name: 'noti_worksheet',
      size: 24,
    }
  }

  switch (case2) {
    case 'EMAIL': {
      return {
        name: 'message',
        size: 24,
      }
    }
    case 'GROUP_REPORT_CREATE':
    case 'GROUP_REPORT_UPDATE':
    case 'ONE_CLICK_REPORT':
    case 'ONE_CLICK_REPORT_CAS':
    case 'EXAM_REPORT':
    case 'ACHIEVEMENT_REPORT_CREATE':
    case 'ESSAY_REPORT':
    default: {
      return {
        name: 'report',
        size: 24,
      }
    }
  }
}

// 그룹 서술형 보고서 zip 은 알림 payload id(uuid)로 다운로드 URL 을 그때그때 발급받는다 (90일 보관)
const downloadGroupEssayReport = async (uuid: string) => {
  try {
    const file = await essayScoringApi.getEssayGroupReportFile({ uuid })
    await downloadFileByUrl(file.presignedUrl, { fullFileName: file.fileName })
  } catch (e) {
    commonErrorMessage(e as Parameters<typeof commonErrorMessage>[0])
  }
}

// TODO: use 훅쓰려면 감싸는 함수는 첫문자 대문자 rule때문에 대문자로 그침
// 여기 타입이랑.. 한번 리팩토링해야할듯
const GetCtaOptions = (case2, sortKey, ctas, id, payload): NotificationCTAOption[] => {
  const service = useRepository(NotificationService)
  const lessonGroupService = useRepository(LessonGroupService)
  const reportListService = useRepository(ReportListService)
  const studentGroupAccordionService = useRepository(
    [StudentGroupAccordionService, RouteName.lesson],
    RouteName.lesson,
  )
  const achievementAnalysisService = useRepository(AchievementAnalysisService)

  // 수업(Lesson) 하위 경로는 좌측 그룹 선택 상태와 그룹별 마지막 경로를 함께 맞춰야 화면이 정상 진입한다.
  // 그룹 판별과 마지막 경로 저장은 쿼리를 뗀 pathname 으로 한다 — 쿼리는 진입 1회성 지시(채점 시트 열기 등)라 되돌아올 때 재실행되면 안 된다
  const moveToLessonPath = async (url: string) => {
    const [pathname] = url.split('?')
    lessonGroupService.setLessonLastPath(pathname)
    const newGroupInfo = await LessonGroupService.getUrlGroupInfoFromPath(pathname)
    studentGroupAccordionService.setSelectedGroupInfo(newGroupInfo)
    if (newGroupInfo) {
      lessonGroupService.groupLastPath.set(newGroupInfo.key, pathname)
      // Lesson 라우트 리스너는 grade/lesson-class 진입 때만 좌측 탭을 맞추고 student 는 직전 탭을 유지한다.
      // 알림으로 학생에 진입할 땐 학년 탭 아래에서 학생이 보여야 하므로 여기서 맞춘다
      if (newGroupInfo.data.type === 'student') {
        studentGroupAccordionService.changeSortType('학년')
      }
    }

    commonRepo.router.push(url)
    service.close()
  }

  const moveToLink = async (link: string) => {
    if (!isInAppPath(link)) return

    if (LessonGroupService.isLessonPage(link)) {
      await moveToLessonPath(link)
      return
    }
    commonRepo.router.push(link)
    service.close()
  }

  const func: (cta: NotificationCta) => NotificationCTAOption = ({ action, text, data }) => {
    switch (action) {
      case 'NONE': {
        return {}
      }
      case 'READ': {
        return {
          onCTAClick: async (_e: MouseEvent) => {
            service.handleItemClick(sortKey)

            if (data?.link) {
              await moveToLink(data.link)
              return
            }

            if (
              (payload.eventType === 'WEAK_WORKSHEET_BY_WEAK_CONCEPT_CHIP' ||
                payload.eventType === 'WEAK_WORKSHEET' ||
                payload.eventType === 'GROUP_WEAK_WORKSHEET_BY_WEAK_CONCEPT_CHIP_CREATE') &&
              payload.groupKey &&
              payload.groupType
            ) {
              await moveToLessonPath(
                mathflatUtils.generateWeakWorksheetPath(payload.groupType, payload.groupKey),
              )
              return
            }

            if (
              payload.type === 'MATHFLAT_SINGLE_REPORT_PREVIEW' &&
              payload.groupKey &&
              payload.groupType
            ) {
              await moveToLessonPath(
                mathflatUtils.generateAchievementAnalysisPath(payload.groupType, payload.groupKey),
              )
              return
            }

            if (payload.groupKey && payload.groupType) {
              await moveToLessonPath(
                mathflatUtils.generateReportPath(payload.groupType, payload.groupKey),
              )
            }
          },
        }
      }
      case 'FILE_DOWNLOAD': {
        return {
          text,
          onCTAClick: (_e: MouseEvent) => {
            service.deleteCTA({ sortKey, action: 'FILE_DOWNLOAD' })
          },
        }
      }
      case 'FILE_DOWNLOAD_API': {
        return {
          text,
          onCTAClick: (_e: MouseEvent) => {
            if (case2 === 'ESSAY_REPORT') {
              void downloadGroupEssayReport(id)
            } else if (case2 === 'EXAM_REPORT') {
              reportListService.downloadGroupExamReport(id)
            } else if (case2 === 'ONE_CLICK_REPORT_CAS') {
              reportListService.downloadOrExportCas(id)
            } else {
              reportListService.downloadOrExport(id)
            }
          },
        }
      }
      case 'FILE_DOWNLOAD_ID_LIST': {
        return {
          text: WebviewService.isAppAndroid ? '내보내기' : text,
          onCTAClick: (e: MouseEvent) => {
            e.stopPropagation()
            if (WebviewService.isAppAndroid) {
              appPostMsg.exportReport({ reportIds: payload.ids, groupName: payload.groupName })
            } else {
              reportListService.download(payload.ids, payload.groupName)
            }
            service.deleteCTA({ sortKey, action: 'FILE_DOWNLOAD_API' })
          },
        }
      }
      case 'PREVIEW': {
        return {
          text,
          onCTAClick: (e: MouseEvent) => {
            e.stopPropagation()
            achievementAnalysisService.preview(payload.id)
          },
        }
      }
      case 'CLIENT_NOTIFICATION_PUBLIC_WORKBOOK_OPEN': {
        return {
          text,
          onCTAClick: (e: MouseEvent) => {
            e.stopPropagation()
            mixpanelClient.track('시중교재전체지원_진입점_알림')
            service.handlePublicWorkbookNotificationItemClick(sortKey)
            commonRepo.router.push(시중교재전체지원_콘텐츠업데이트_경로)
            service.close()
          },
        }
      }
      case 'CLIENT_NOTIFICATION_ESSAY_WORKSHEET_OPEN': {
        return {
          text,
          onCTAClick: (e: MouseEvent) => {
            e.stopPropagation()
            mixpanelClient.track('서술형_진입점_알림')
            service.handleEssayNotificationItemClick(sortKey)
            commonRepo.router.push(수능_콘텐츠_업데이트_경로)
            service.close()
          },
        }
      }
      default: {
        return {}
      }
    }
  }

  return ctas.map(func)
}

export default observer(Notification)
